
import React, { useState } from 'react';
import { 
  Shield, 
  Activity, 
  Terminal, 
  Key, 
  Users, 
  Lock, 
  Zap, 
  UserPlus, 
  Search, 
  MoreVertical, 
  Filter, 
  Globe, 
  Database,
  Cpu,
  ArrowUpRight,
  TrendingDown,
  CloudLightning,
  RefreshCw,
  Power,
  CreditCard,
  DollarSign,
  TrendingUp,
  Download,
  Mail,
  Ban,
  Settings,
  ShieldCheck,
  Briefcase,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  User,
  Building,
  Wrench,
  ArrowRight,
  Trash2,
  History,
  Coins,
  Store,
  Layers,
  Edit2,
  Plus
} from 'lucide-react';

type AdminTab = 'overview' | 'customers' | 'resellers' | 'billing' | 'team' | 'providers' | 'audit' | 'system' | 'tools';

export const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');

  return (
    <div className="h-full flex flex-col bg-slate-50 animate-in fade-in duration-500">
      {/* Sub-Header / Admin Nav */}
      <div className="bg-white border-b border-slate-200 px-6 pt-6 sticky top-0 z-20">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center space-x-2">
              <Shield className="text-blue-600" size={28} />
              <span>SaaS Command Center</span>
            </h1>
            <p className="text-slate-500 text-sm">Global platform oversight, customer success & revenue management.</p>
          </div>
          <div className="flex items-center space-x-3">
             <div className="bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100 flex items-center space-x-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-black text-emerald-700 uppercase">Platform Status: Healthy</span>
             </div>
             <button className="bg-slate-900 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-slate-800 transition-all flex items-center space-x-2">
                <RefreshCw size={14} />
                <span>Global Sync</span>
             </button>
          </div>
        </div>

        <div className="flex space-x-8 overflow-x-auto no-scrollbar">
          <AdminNavButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} label="Overview" icon={<Cpu size={18} />} />
          <AdminNavButton active={activeTab === 'customers'} onClick={() => setActiveTab('customers')} label="Customers" icon={<Globe size={18} />} />
          <AdminNavButton active={activeTab === 'resellers'} onClick={() => setActiveTab('resellers')} label="Resellers" icon={<Store size={18} />} />
          <AdminNavButton active={activeTab === 'tools'} onClick={() => setActiveTab('tools')} label="Diagnostic Tools" icon={<Wrench size={18} />} />
          <AdminNavButton active={activeTab === 'billing'} onClick={() => setActiveTab('billing')} label="Billing & Plans" icon={<DollarSign size={18} />} />
          <AdminNavButton active={activeTab === 'team'} onClick={() => setActiveTab('team')} label="Internal Team" icon={<Users size={18} />} />
          <AdminNavButton active={activeTab === 'providers'} onClick={() => setActiveTab('providers')} label="API Providers" icon={<Database size={18} />} />
          <AdminNavButton active={activeTab === 'audit'} onClick={() => setActiveTab('audit')} label="Audit" icon={<Terminal size={18} />} />
          <AdminNavButton active={activeTab === 'system'} onClick={() => setActiveTab('system')} label="System" icon={<Lock size={18} />} />
        </div>
      </div>

      <div className="flex-1 p-6 overflow-y-auto">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'customers' && <CustomersTab />}
        {activeTab === 'resellers' && <ResellersTab />}
        {activeTab === 'tools' && <DiagnosticToolsTab />}
        {activeTab === 'billing' && <BillingTab />}
        {activeTab === 'team' && <UsersTab />}
        {activeTab === 'providers' && <ProvidersTab />}
        {activeTab === 'audit' && <AuditTab />}
        {activeTab === 'system' && <SystemTab />}
      </div>
    </div>
  );
};

const ResellersTab: React.FC = () => (
  <div className="space-y-6 animate-in fade-in duration-300">
    <div className="flex justify-between items-center">
       <div>
          <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">White-Label Partnerships</h3>
          <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Manage Reseller accounts and credit allocation</p>
       </div>
       <button className="bg-blue-600 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 shadow-lg flex items-center space-x-2">
          <Plus size={16} />
          <span>Add New Reseller</span>
       </button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
       {[
         { name: 'ComplianceLabs S.A.', tenants: 24, credits: '850,000', revenue: '$14.2k', tier: 'Gold' },
         { name: 'RegTech Nordic', tenants: 12, credits: '240,000', revenue: '$8.4k', tier: 'Silver' },
       ].map((r, i) => (
         <div key={i} className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm hover:border-blue-300 transition-all group">
            <div className="flex justify-between items-start mb-6">
               <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-sm">
                  <Store size={24} />
               </div>
               <span className="text-[10px] font-black bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full border border-emerald-100 uppercase">{r.tier} Level</span>
            </div>
            <h4 className="text-xl font-black text-slate-900 mb-1">{r.name}</h4>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">{r.tenants} Sub-tenants Active</p>
            
            <div className="space-y-4 pt-6 border-t border-slate-100">
               <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-slate-400 uppercase">Available Credits</span>
                  <span className="text-sm font-black text-slate-900">{r.credits}</span>
               </div>
               <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-slate-400 uppercase">Reseller Margin</span>
                  <span className="text-sm font-black text-blue-600">35%</span>
               </div>
            </div>
            
            <div className="mt-8 flex space-x-2">
               <button className="flex-1 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all">Manage Account</button>
               <button className="px-4 py-3 bg-white border border-slate-200 text-slate-400 rounded-xl hover:text-blue-600 transition-all"><Edit2 size={16} /></button>
            </div>
         </div>
       ))}
    </div>
  </div>
);

const BillingTab: React.FC = () => (
  <div className="space-y-8 animate-in fade-in duration-300">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
       <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex justify-between items-start mb-4">
             <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl"><DollarSign size={20} /></div>
             <span className="text-[10px] font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full uppercase">Target: 110%</span>
          </div>
          <p className="text-3xl font-black text-slate-900">$1,482,000</p>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Total Contract Value (ARR)</p>
       </div>
       <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex justify-between items-start mb-4">
             <div className="p-2 bg-blue-50 text-blue-600 rounded-xl"><CreditCard size={20} /></div>
             <span className="text-[10px] font-black text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full uppercase">Alert: 3 Failed</span>
          </div>
          <p className="text-3xl font-black text-slate-900">$8,402</p>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Net Revenue This Week</p>
       </div>
       <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex justify-between items-start mb-4">
             <div className="p-2 bg-amber-50 text-amber-600 rounded-xl"><TrendingUp size={20} /></div>
             <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full uppercase">LTV: $14.2k</span>
          </div>
          <p className="text-3xl font-black text-slate-900">4.8%</p>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Churn Rate (30 Days)</p>
       </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
       {/* New Plan Management Section */}
       <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden h-fit">
          <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/30">
             <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center">
                <Layers size={16} className="mr-2 text-blue-600" /> Subscription Plan Architect
             </h3>
             <button className="p-2.5 bg-white border border-slate-200 rounded-xl text-blue-600 hover:bg-slate-50 transition-all shadow-sm">
                <Plus size={18} />
             </button>
          </div>
          <div className="p-8 space-y-4">
             {[
               { name: 'Free Tier', price: '$0', calls: '100', status: 'Published' },
               { name: 'Pro Intelligence', price: '$249', calls: '10,000', status: 'Published' },
               { name: 'Enterprise Elite', price: '$999', calls: 'Unlimited', status: 'Published' },
             ].map((plan, i) => (
               <div key={i} className="p-5 border border-slate-100 rounded-2xl hover:border-blue-300 hover:bg-slate-50 transition-all group">
                  <div className="flex justify-between items-center mb-4">
                     <div>
                        <p className="text-sm font-black text-slate-900">{plan.name}</p>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{plan.price} / month</p>
                     </div>
                     <span className="text-[8px] font-black bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded uppercase border border-emerald-100">Live</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">
                     <span>API Quota: {plan.calls}</span>
                     <button className="text-blue-600 hover:underline">Edit Features</button>
                  </div>
                  <div className="w-full bg-slate-200 h-1 rounded-full"></div>
               </div>
             ))}
          </div>
       </div>

       <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
             <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Recent Platform Invoices</h3>
             <button className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline">Stripe Activity</button>
          </div>
          <div className="divide-y divide-slate-100">
             {[
               { id: 'INV-A-1240', cust: 'Acme Financial', date: 'Just Now', amount: '$999.00', status: 'Succeeded' },
               { id: 'INV-A-1239', cust: 'Nebula Ventures', date: '12m ago', amount: '$249.00', status: 'Failed' },
               { id: 'INV-A-1238', cust: 'Titan Global', date: '1h ago', amount: '$999.00', status: 'Succeeded' },
               { id: 'INV-A-1237', cust: 'BlueChip Crypto', date: '2h ago', amount: '$249.00', status: 'Succeeded' },
             ].map((inv, i) => (
               <div key={i} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-all">
                  <div className="flex items-center space-x-4">
                     <div className={`p-2 rounded-lg ${inv.status === 'Failed' ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}>
                        {inv.status === 'Failed' ? <Ban size={16} /> : <CheckCircle2 size={16} />}
                     </div>
                     <div>
                        <p className="text-sm font-bold text-slate-900">{inv.cust}</p>
                        <p className="text-[10px] font-black text-slate-400 uppercase">{inv.id} • {inv.date}</p>
                     </div>
                  </div>
                  <div className="text-right">
                     <p className="text-sm font-black text-slate-900">{inv.amount}</p>
                     <p className={`text-[10px] font-black uppercase ${inv.status === 'Failed' ? 'text-rose-600' : 'text-emerald-600'}`}>{inv.status}</p>
                  </div>
               </div>
             ))}
          </div>
       </div>
    </div>
  </div>
);

const OverviewTab: React.FC = () => (
  <div className="space-y-8 animate-in slide-in-from-bottom-2 duration-400">
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      <AdminStatCard icon={<DollarSign className="text-emerald-600" />} label="Monthly Rev" value="$142.5k" sub="MRR Growth +12%" trend={8} />
      <AdminStatCard icon={<Globe className="text-blue-600" />} label="Active Customers" value="842" sub="Across 14 regions" trend={15} />
      <AdminStatCard icon={<Activity className="text-amber-600" />} label="API Traffic" value="8.4M" sub="Requests / day" trend={24} />
      <AdminStatCard icon={<ShieldCheck className="text-indigo-600" />} label="Security Score" value="99.9%" sub="Uptime & DLP" trend={1} />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-6">Revenue & Conversion Trend</h3>
        <div className="h-64 flex items-end space-x-1">
          {Array.from({ length: 40 }).map((_, i) => (
            <div 
              key={i} 
              className="flex-1 bg-emerald-100 hover:bg-emerald-500 rounded-t-sm transition-all cursor-crosshair" 
              style={{ height: `${30 + ((i * 13) % 70)}%` }}
            ></div>
          ))}
        </div>
      </div>
      <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl shadow-slate-200 border border-slate-800 flex flex-col">
        <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center">
          <Terminal size={16} className="mr-2" /> Global Traffic Ingress
        </h3>
        <div className="flex-1 space-y-3 font-mono text-[10px] text-emerald-400 overflow-y-auto no-scrollbar">
          <p><span className="text-slate-500">[16:44:01]</span> REQ: 200 OK - /api/v1/aml/search (Cust: AcmeCorp)</p>
          <p><span className="text-slate-500">[16:44:12]</span> PAY: Webhook received - stripe.invoice.paid</p>
          <p><span className="text-slate-500">[16:44:45]</span> AUTH: 401 Unauthorized - Invalid Token (IP: 45.2.11.9)</p>
          <p><span className="text-slate-500">[16:45:02]</span> SYNC: ESG indices updated from Opoint</p>
          <p className="animate-pulse text-blue-400">{" >> "} listening on port 443...</p>
        </div>
      </div>
    </div>
  </div>
);

const DiagnosticToolsTab: React.FC = () => {
  const [iban, setIban] = useState('');
  const [payeeName, setPayeeName] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [historyList, setHistoryList] = useState<any[]>([]);

  const handleVerifyIBAN = () => {
    if (!iban) return;
    setIsVerifying(true);
    setTimeout(() => {
      const match = payeeName.toLowerCase().includes('global') || !payeeName;
      const newResult = {
        isValid: true,
        bank: 'Barclays Bank PLC',
        country: 'United Kingdom',
        bic: 'BARCGB22',
        currency: 'GBP',
        payeeMatch: match ? 'Exact Match' : 'Potential Mismatch',
        accountStatus: 'Active / Reachable',
        verificationId: `ADM-IBAN-${Math.floor(Math.random() * 10000)}`,
        timestamp: new Date().toLocaleTimeString(),
        ibanRequested: iban,
        payeeRequested: payeeName || 'N/A'
      };
      setResult(newResult);
      setHistoryList(prev => [newResult, ...prev].slice(0, 5));
      setIsVerifying(false);
    }, 1500);
  };

  const handleReset = () => {
    setIban('');
    setPayeeName('');
    setResult(null);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* IBAN Monitoring Section */}
        <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="p-8 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center">
                <CreditCard className="mr-2 text-blue-600" size={20} /> IBAN Monitoring Diagnostic
              </h3>
              <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">Admin Internal Payee Verification</p>
            </div>
            <button 
              onClick={handleReset}
              className="p-2 text-slate-400 hover:text-rose-600 transition-colors"
              title="Reset Form"
            >
              <Trash2 size={20} />
            </button>
          </div>
          
          <div className="p-8 space-y-6 flex-1">
            <div className="grid grid-cols-1 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Account Holder Name</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input 
                    type="text" 
                    value={payeeName}
                    onChange={(e) => setPayeeName(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" 
                    placeholder="e.g. Acme Global Holdings"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">IBAN Number</label>
                <div className="relative">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input 
                    type="text" 
                    value={iban}
                    onChange={(e) => setIban(e.target.value.toUpperCase())}
                    className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-mono focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all outline-none" 
                    placeholder="GB29 BARC 2039 4483 1284"
                  />
                </div>
              </div>
            </div>

            <button 
              onClick={handleVerifyIBAN}
              disabled={isVerifying || !iban}
              className="w-full py-4 bg-slate-900 text-white rounded-[1.5rem] text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center space-x-2 shadow-xl shadow-slate-200"
            >
              {isVerifying ? (
                <RefreshCw size={16} className="animate-spin" />
              ) : (
                <>
                  <ShieldCheck size={16} />
                  <span>Verify Account Status</span>
                </>
              )}
            </button>

            {result && (
              <div className="animate-in zoom-in-95 duration-300 mt-8 p-6 bg-slate-900 rounded-3xl text-white shadow-2xl relative overflow-hidden">
                <div className="relative z-10 space-y-6">
                  <div className="flex justify-between items-center border-b border-white/10 pb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Verification Success</span>
                    </div>
                    <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest">{result.verificationId}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-y-6 gap-x-4">
                    <DiagnosticItem label="Bank Name" value={result.bank} icon={<Building size={12}/>} />
                    <DiagnosticItem label="Country" value={result.country} icon={<Globe size={12}/>} />
                    <DiagnosticItem label="SWIFT / BIC" value={result.bic} icon={<Cpu size={12}/>} />
                    <DiagnosticItem label="Currency" value={result.currency} icon={<Coins size={12}/>} />
                    <DiagnosticItem 
                      label="Account Status" 
                      value={result.accountStatus} 
                      color="text-emerald-400" 
                      icon={<CheckCircle2 size={12}/>}
                    />
                    <DiagnosticItem 
                      label="Payee Match" 
                      value={result.payeeMatch} 
                      color={result.payeeMatch.includes('Mismatch') ? 'text-rose-400' : 'text-emerald-400'} 
                      icon={<User size={12}/>}
                    />
                  </div>
                </div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600 rounded-full blur-[60px] opacity-20 -translate-y-1/2 translate-x-1/2"></div>
              </div>
            )}
          </div>
        </div>

        {/* Blockchain & KYB Health Section */}
        <div className="space-y-6">
          <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-6 flex items-center">
              <Zap size={18} className="mr-2 text-amber-500" /> API Infrastructure Health
            </h3>
            <div className="space-y-4">
              <HealthStatusItem label="Blockchain Crystal-API" status="Operational" latency="42ms" />
              <HealthStatusItem label="Corporate Registry (KYB)" status="Degraded" latency="850ms" isWarning />
              <HealthStatusItem label="IBAN SEPA-Gateway" status="Operational" latency="120ms" />
              <HealthStatusItem label="Confirmation of Payee Node" status="Operational" latency="18ms" />
            </div>
          </div>

          <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
             <div className="mb-6 flex items-center justify-between">
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center">
                   <History size={16} className="mr-2" /> Recent Internal History
                </h3>
                {historyList.length > 0 && <span className="text-[9px] font-black bg-blue-50 text-blue-600 px-2 py-0.5 rounded uppercase">Real-time</span>}
             </div>
             <div className="space-y-3 overflow-y-auto max-h-[280px] no-scrollbar">
                {historyList.length === 0 ? (
                  <div className="text-center py-10 opacity-30">
                     <History size={32} className="mx-auto mb-2" />
                     <p className="text-[10px] font-black uppercase tracking-widest">No recent lookups</p>
                  </div>
                ) : (
                  historyList.map((h, i) => (
                    <div key={i} className="p-4 bg-slate-50 border border-slate-100 rounded-2xl flex justify-between items-center group hover:bg-white hover:border-blue-200 transition-all cursor-default">
                       <div className="space-y-1">
                          <p className="text-xs font-black text-slate-900 leading-none">{h.payeeRequested}</p>
                          <p className="text-[10px] font-mono text-slate-400 uppercase">{h.ibanRequested.substring(0, 14)}...</p>
                       </div>
                       <div className="text-right space-y-1">
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-tight">{h.timestamp}</p>
                          <p className={`text-[9px] font-black uppercase ${h.payeeMatch.includes('Mismatch') ? 'text-rose-600' : 'text-emerald-600'}`}>{h.payeeMatch.split(' ')[0]}</p>
                       </div>
                    </div>
                  ))
                )}
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DiagnosticItem: React.FC<{ label: string, value: string, color?: string, icon: React.ReactNode }> = ({ label, value, color = 'text-white', icon }) => (
  <div className="group">
    <div className="flex items-center space-x-1.5 mb-1 opacity-40 group-hover:opacity-100 transition-opacity">
      <span className="text-blue-400">{icon}</span>
      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
    </div>
    <p className={`text-xs font-bold leading-tight ${color}`}>{value}</p>
  </div>
);

const HealthStatusItem: React.FC<{ label: string, status: string, latency: string, isWarning?: boolean }> = ({ label, status, latency, isWarning }) => (
  <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 border border-slate-100">
    <div className="flex items-center space-x-3">
      <div className={`w-2 h-2 rounded-full ${isWarning ? 'bg-amber-500' : 'bg-emerald-500'} animate-pulse`}></div>
      <span className="text-xs font-bold text-slate-700">{label}</span>
    </div>
    <div className="text-right">
       <p className={`text-[10px] font-black uppercase ${isWarning ? 'text-amber-600' : 'text-emerald-600'}`}>{status}</p>
       <p className="text-[9px] font-bold text-slate-400 uppercase">{latency}</p>
    </div>
  </div>
);

const CustomersTab: React.FC = () => (
  <div className="space-y-6 animate-in fade-in duration-300">
    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
      <div className="flex items-center space-x-3 bg-white px-4 py-2.5 rounded-xl border border-slate-200 w-full md:w-96 shadow-sm">
        <Search size={18} className="text-slate-400" />
        <input type="text" placeholder="Search customers by company or domain..." className="text-sm outline-none w-full font-medium" />
      </div>
      <div className="flex space-x-2">
        <button className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50">
          Export CSV
        </button>
        <button className="bg-blue-600 text-white px-4 py-2.5 rounded-xl text-sm font-bold flex items-center space-x-2 shadow-lg shadow-blue-200 hover:bg-blue-700">
          <UserPlus size={18} />
          <span>Invite New Customer</span>
        </button>
      </div>
    </div>

    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <table className="w-full text-left">
        <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
          <tr>
            <th className="px-6 py-4">Company / Workspace</th>
            <th className="px-6 py-4">Subscription Plan</th>
            <th className="px-6 py-4">API Usage</th>
            <th className="px-6 py-4">Status</th>
            <th className="px-6 py-4">MRR Contribution</th>
            <th className="px-6 py-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {[
            { name: 'Acme Financial', domain: 'acmefin.com', plan: 'Enterprise', usage: '85%', status: 'Active', mrr: '$999', users: 124 },
            { name: 'BlueChip Crypto', domain: 'bc-crypto.io', plan: 'Pro', usage: '42%', status: 'Active', mrr: '$249', users: 12 },
            { name: 'Nebula Ventures', domain: 'nebula.vc', plan: 'Pro', usage: '92%', status: 'Past Due', mrr: '$249', users: 8 },
            { name: 'SmallBatch Co', domain: 'smallbatch.dev', plan: 'Free', usage: '12%', status: 'Active', mrr: '$0', users: 2 },
            { name: 'Titan Global', domain: 'titan.com', plan: 'Enterprise', usage: '15%', status: 'Active', mrr: '$999', users: 442 },
          ].map((cust, i) => (
            <tr key={i} className="hover:bg-slate-50/50 transition-all group">
              <td className="px-6 py-4">
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-xl bg-slate-900 flex items-center justify-center text-white font-black text-xs uppercase">
                    {cust.name.substring(0, 2)}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{cust.name}</p>
                    <p className="text-[10px] font-black text-slate-400 uppercase">{cust.domain} • {cust.users} seats</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                  cust.plan === 'Enterprise' ? 'bg-indigo-50 text-indigo-600 border-indigo-100' :
                  cust.plan === 'Pro' ? 'bg-blue-50 text-blue-600 border-blue-100' :
                  'bg-slate-100 text-slate-500'
                }`}>
                  {cust.plan}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center space-x-2">
                   <div className="flex-1 w-20 bg-slate-100 h-1.5 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${parseInt(cust.usage) > 80 ? 'bg-rose-500' : 'bg-blue-500'}`} style={{ width: cust.usage }}></div>
                   </div>
                   <span className="text-[10px] font-black text-slate-600">{cust.usage}</span>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className={`inline-flex items-center space-x-1.5 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                  cust.status === 'Active' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${cust.status === 'Active' ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                  <span>{cust.status}</span>
                </span>
              </td>
              <td className="px-6 py-4">
                 <span className="text-sm font-black text-slate-900">{cust.mrr}</span>
              </td>
              <td className="px-6 py-4 text-right">
                <div className="flex justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title="Login as User">
                    <Power size={16} />
                  </button>
                  <button className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all">
                    <Settings size={16} />
                  </button>
                  <button className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all">
                    <Ban size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const UsersTab: React.FC = () => (
  <div className="space-y-6 animate-in fade-in duration-300">
    <div className="flex justify-between items-center">
      <div className="flex items-center space-x-3 bg-white px-4 py-2 rounded-xl border border-slate-200 w-96 shadow-sm">
        <Search size={16} className="text-slate-400" />
        <input type="text" placeholder="Search team members by name or role..." className="text-sm outline-none w-full font-medium" />
      </div>
      <button className="bg-blue-600 text-white px-4 py-2.5 rounded-xl text-sm font-bold flex items-center space-x-2 shadow-lg shadow-blue-200 hover:bg-blue-700">
        <UserPlus size={18} />
        <span>Add Internal Admin</span>
      </button>
    </div>

    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <table className="w-full text-left">
        <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
          <tr>
            <th className="px-6 py-4">Officer / Identity</th>
            <th className="px-6 py-4">Access Role</th>
            <th className="px-6 py-4">Status</th>
            <th className="px-6 py-4 text-center">Case Resolution</th>
            <th className="px-6 py-4">Last Activity</th>
            <th className="px-6 py-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {[
            { name: 'Julian Casablancas', email: 'julian@minerva.com', role: 'Platform Admin', status: 'Active', res: '98%', last: 'Now' },
            { name: 'Albert Hammond', email: 'albert@minerva.com', role: 'Support Tier 2', status: 'Active', res: '84%', last: '12m ago' },
            { name: 'Nick Valensi', email: 'nick@minerva.com', role: 'Billing Mgr', status: 'Active', res: '76%', last: '2d ago' },
            { name: 'Fabrizio Moretti', email: 'fab@minerva.com', role: 'Security Auditor', status: 'Active', res: '92%', last: '1h ago' },
          ].map((user, i) => (
            <tr key={i} className="hover:bg-slate-50/50 transition-all">
              <td className="px-6 py-4">
                <div className="flex items-center space-x-3">
                  <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${user.name}`} className="w-8 h-8 rounded-lg bg-slate-100" />
                  <div>
                    <p className="text-sm font-bold text-slate-900">{user.name}</p>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">{user.email}</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className="text-xs font-bold text-slate-700">{user.role}</span>
              </td>
              <td className="px-6 py-4">
                <div className={`inline-flex items-center space-x-1.5 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                  user.status === 'Active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${user.status === 'Active' ? 'bg-emerald-500' : 'bg-slate-400'}`}></div>
                  <span>{user.status}</span>
                </div>
              </td>
              <td className="px-6 py-4 text-center">
                 <div className="flex flex-col items-center">
                    <span className="text-xs font-black text-slate-900">{user.res}</span>
                    <div className="w-16 bg-slate-100 h-1 rounded-full mt-1">
                       <div className="bg-blue-600 h-full rounded-full" style={{ width: user.res }}></div>
                    </div>
                 </div>
              </td>
              <td className="px-6 py-4">
                <span className="text-xs text-slate-500 font-medium">{user.last}</span>
              </td>
              <td className="px-6 py-4 text-right">
                <button className="p-2 text-slate-400 hover:bg-white rounded-xl transition-all hover:shadow-sm border border-transparent hover:border-slate-100">
                  <MoreVertical size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ProvidersTab: React.FC = () => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in duration-300">
    <div className="space-y-6">
       <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest flex items-center">
         <Zap size={18} className="mr-2 text-blue-600" /> API Gateway Management
       </h3>
       <div className="space-y-4">
          {[
            { name: 'Crystal API (AML/Crypto)', cost: '$0.042/call', status: 'Connected', usage: '4,202 / 10k', color: 'blue' },
            { name: 'Opoint (ESG Scoring)', cost: '$0.025/call', status: 'Connected', usage: '1,500 / 50k', color: 'emerald' },
            { name: 'ESPY.com (Sentiment)', cost: '$0.012/call', status: 'Degraded', usage: '12k / Unlimited', color: 'amber' },
            { name: 'EmailVerify.io', cost: '$0.005/call', status: 'Connected', usage: '802 / 5k', color: 'indigo' },
          ].map((api, i) => (
            <div key={i} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:border-blue-300 transition-all group">
               <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-3">
                     <div className={`p-2 bg-${api.color}-50 text-${api.color}-600 rounded-xl`}>
                        <Globe size={20} />
                     </div>
                     <div>
                        <h4 className="text-sm font-black text-slate-900">{api.name}</h4>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{api.cost}</p>
                     </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${
                    api.status === 'Connected' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                  }`}>
                    {api.status}
                  </div>
               </div>
               <div className="space-y-2">
                  <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <span>Usage Quota</span>
                    <span>{api.usage}</span>
                  </div>
                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                    <div className={`bg-${api.color}-500 h-full rounded-full transition-all duration-1000`} style={{ width: '40%' }}></div>
                  </div>
               </div>
               <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end space-x-3">
                  <button className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-rose-600">Disconnect</button>
                  <button className="text-[10px] font-black uppercase tracking-widest text-blue-600 hover:underline">Manage Key</button>
               </div>
            </div>
          ))}
       </div>
    </div>

    <div className="space-y-6">
       <div className="bg-slate-900 rounded-2xl p-6 text-white h-full border border-slate-800 shadow-2xl">
          <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center">
             <TrendingDown size={16} className="mr-2 text-rose-400" /> Infrastructure Cost Analysis
          </h3>
          <div className="flex flex-col justify-between h-full space-y-8">
             <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                   <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Provider Spend</p>
                   <p className="text-2xl font-black text-white">$4,282.40</p>
                   <p className="text-[10px] text-emerald-400 font-bold mt-1">+12% vs last month</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                   <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Infrastructure Projection</p>
                   <p className="text-2xl font-black text-white">$51,388</p>
                   <p className="text-[10px] text-amber-400 font-bold mt-1">Budget Alert: 84%</p>
                </div>
             </div>
             
             <div className="space-y-4">
                <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Top Cost Sources</h4>
                {[
                  { label: 'Crystal Compliance', p: 54, color: 'bg-blue-500' },
                  { label: 'Opoint Sustainability', p: 32, color: 'bg-emerald-500' },
                  { label: 'ESPY Sentiment', p: 14, color: 'bg-amber-500' },
                ].map((cost, i) => (
                  <div key={i} className="space-y-1">
                     <div className="flex justify-between text-[10px] font-bold">
                        <span>{cost.label}</span>
                        <span>{cost.p}%</span>
                     </div>
                     <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
                        <div className={`${cost.color} h-full`} style={{ width: `${cost.p}%` }}></div>
                     </div>
                  </div>
                ))}
             </div>

             <button className="w-full py-3 bg-blue-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-blue-700 shadow-lg shadow-blue-500/20">
               Generate Detailed Financial Report
             </button>
          </div>
       </div>
    </div>
  </div>
);

const AuditTab: React.FC = () => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in duration-300">
    <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
       <div className="flex space-x-2">
          <button className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 flex items-center space-x-2">
             <Filter size={14} />
             <span>Filter Severity</span>
          </button>
          <button className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 flex items-center space-x-2">
             <CalendarClock size={14} />
             <span>Last 24 Hours</span>
          </button>
       </div>
       <div className="text-[10px] font-black text-slate-400 uppercase">1,240 Entries Recorded</div>
    </div>
    <div className="divide-y divide-slate-100">
      {[1,2,3,4,5,6,7,8].map(i => (
        <div key={i} className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between group">
           <div className="flex items-center space-x-4">
              <div className={`p-2 rounded-lg ${i % 3 === 0 ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-500'}`}>
                 <Terminal size={18} />
              </div>
              <div>
                 <div className="flex items-center space-x-2 mb-1">
                    <span className="text-sm font-bold text-slate-900">Admin Action Logged</span>
                    <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded ${i % 3 === 0 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                       {i % 3 === 0 ? 'Review' : 'Verified'}
                    </span>
                 </div>
                 <p className="text-xs text-slate-500">Julian C. updated plan for {"'"}AcmeCorp{"'"} (Standard {" -> "} Enterprise)</p>
              </div>
           </div>
           <div className="text-right">
              <p className="text-[10px] font-black text-slate-400 uppercase">Oct 27, 2023</p>
              <p className="text-xs text-slate-500">16:4{i}:22</p>
           </div>
        </div>
      ))}
    </div>
  </div>
);

const SystemTab: React.FC = () => (
  <div className="max-w-4xl mx-auto space-y-8 animate-in slide-in-from-right duration-400">
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
       <div className="p-6 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">Platform Infrastructure</h3>
          <p className="text-sm text-slate-500">Manage global settings, geofencing, and gateway failovers.</p>
       </div>
       <div className="p-6 space-y-6">
          <SystemSwitch 
            icon={<Power className="text-rose-500" />} 
            title="SaaS Maintenance Mode" 
            desc="Locks all tenant dashboards and halts billing webhooks for core updates." 
          />
          <SystemSwitch 
            icon={<Shield className="text-blue-500" />} 
            title="DDoS Protection Engine" 
            desc="Enable Cloudflare Enterprise level scrubbing for all inbound API calls." 
            initialState={true}
          />
          <SystemSwitch 
            icon={<CloudLightning className="text-amber-500" />} 
            title="Circuit Breaker Protocol" 
            desc="Automatically cut provider connections if costs exceed safety thresholds." 
            initialState={true}
          />
          <SystemSwitch 
            icon={<RefreshCw className="text-indigo-500" />} 
            title="Automatic Log Purge" 
            desc="Wipe data exceeding GDPR retention policy (365 days) automatically." 
            initialState={true}
          />
       </div>
    </div>

    <div className="bg-rose-50 border border-rose-100 rounded-2xl p-6 flex items-start space-x-4">
       <div className="p-3 bg-white rounded-xl shadow-sm text-rose-600"><Lock size={24} /></div>
       <div>
          <h4 className="text-sm font-black text-rose-900 uppercase tracking-widest mb-1">Critical Platform Lockdown</h4>
          <p className="text-xs text-rose-700 mb-4 leading-relaxed">
            Revoke all JWT tokens and API keys globally. This will disconnect all customers immediately.
          </p>
          <button className="bg-rose-600 text-white px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-rose-700 shadow-lg shadow-rose-200 transition-all active:scale-95">
             Execute Master Revocation
          </button>
       </div>
    </div>
  </div>
);

const AdminNavButton: React.FC<{ active: boolean, onClick: () => void, label: string, icon: React.ReactNode }> = ({ active, onClick, label, icon }) => (
  <button 
    onClick={onClick}
    className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-all whitespace-nowrap ${
      active 
        ? 'border-blue-600 text-blue-600 font-black' 
        : 'border-transparent text-slate-500 font-bold hover:text-slate-900 hover:border-slate-300'
    }`}
  >
    {icon}
    <span className="text-sm uppercase tracking-widest">{label}</span>
  </button>
);

const AdminStatCard: React.FC<{ icon: React.ReactNode, label: string, value: string, sub: string, trend: number }> = ({ icon, label, value, sub, trend }) => (
  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:border-blue-300 transition-all group">
    <div className="flex items-center justify-between mb-4">
      <div className="p-2 bg-slate-50 rounded-xl group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">{icon}</div>
      <div className={`text-xs font-bold flex items-center ${trend > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
        {trend > 0 ? <ArrowUpRight size={14} /> : <TrendingDown size={14} />}
        {Math.abs(trend)}%
      </div>
    </div>
    <div className="text-2xl font-black text-slate-900">{value}</div>
    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{label}</div>
    <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
  </div>
);

const SystemSwitch: React.FC<{ icon: React.ReactNode, title: string, desc: string, initialState?: boolean }> = ({ icon, title, desc, initialState = false }) => {
  const [enabled, setEnabled] = useState(initialState);
  return (
    <div className="flex items-center justify-between py-4 group">
      <div className="flex items-start space-x-4">
        <div className="p-2 bg-slate-50 rounded-lg group-hover:bg-white transition-colors">{icon}</div>
        <div className="max-w-md">
          <p className="text-sm font-bold text-slate-900">{title}</p>
          <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
        </div>
      </div>
      <button 
        onClick={() => setEnabled(!enabled)}
        className={`w-12 h-6 rounded-full relative transition-all shadow-inner ${enabled ? 'bg-blue-600' : 'bg-slate-200'}`}
      >
        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${enabled ? 'left-7' : 'left-1'}`}></div>
      </button>
    </div>
  );
};

const CalendarClock: React.FC<{size: number}> = ({size}) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M16 14v2a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-2"/></svg>
);
